import type { ApolloCache, MutationUpdaterFunction } from "@apollo/client";
import { DocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useAuth } from "@clerk/clerk-react";
import { useCallback, useRef } from "react";

import { Values } from "@/constants";
import { State } from "@/state";
import { Utils } from "@/utils";
import {
  formatApolloError,
  type GraphqlPayloadErrors,
} from "@/utils/graphqlErrors";

export type ErrorHandlersType = {
  authenticationError?: (error) => void;
  authorizationError?: (error) => void;
  rateLimitError?: (error) => void;
  notFoundError?: (error) => void;
  subscriptionError?: (error) => void;
  genericErrors?: (error) => void;
  fieldErrors?: (error) => void;
};

export type CallbacksType<Payload> = {
  onSuccess?: (data: Payload) => void;
  /** Refused, or failed: with what the payload said of it, where it said anything. */
  onError?: (data?: Payload, errors?: GraphqlPayloadErrors) => void;
};

export type MutateBackendType<Input, Payload> = {
  mutation: DocumentNode;
  variables?: Input;
  callbacks?: CallbacksType<Payload>;
  refetchQueries?: (string | DocumentNode)[];
  errorHandlers?: ErrorHandlersType;
  optimisticResponse?: unknown;
  update?: MutationUpdaterFunction<Payload, Input, ApolloCache>;
};

function buildDefaultErrorHandlers(
  setSnackbarMessage: (message: string) => void,
  isAuthenticated: boolean,
): ErrorHandlersType {
  return {
    authenticationError: (error) => {
      setSnackbarMessage(error.message);
    },
    authorizationError: (error) => {
      setSnackbarMessage(error.message);
    },
    rateLimitError: (error) => {
      setSnackbarMessage(error.message);
    },
    notFoundError: (error) => {
      setSnackbarMessage(error.message);
    },
    subscriptionError: (error) => {
      if (isAuthenticated) {
        // TODO: subscribe dialog
      } else {
        setSnackbarMessage(error.message);
      }
    },
    genericErrors: (error) => {
      setSnackbarMessage(error.message);
    },
    fieldErrors: (error) => {
      setSnackbarMessage(
        error.field ? `${error.field}: ${error.message}` : error.message,
      );
    },
  };
}

function handleErrors(
  errors: GraphqlPayloadErrors,
  errorHandlers: ErrorHandlersType,
) {
  if (!errors) {
    return;
  }

  if (errors.authenticationError && errorHandlers.authenticationError) {
    errorHandlers.authenticationError(errors.authenticationError);
  }
  if (errors.authorizationError && errorHandlers.authorizationError) {
    errorHandlers.authorizationError(errors.authorizationError);
  }
  if (errors.rateLimitError && errorHandlers.rateLimitError) {
    errorHandlers.rateLimitError(errors.rateLimitError);
  }
  if (errors.notFoundError && errorHandlers.notFoundError) {
    errorHandlers.notFoundError(errors.notFoundError);
  }
  if (errors.subscriptionError && errorHandlers.subscriptionError) {
    errorHandlers.subscriptionError(errors.subscriptionError);
  }
  if (
    errors.genericErrors &&
    errors.genericErrors.length > 0 &&
    errorHandlers.genericErrors !== undefined
  ) {
    errors.genericErrors.forEach((error) => {
      errorHandlers.genericErrors(error);
    });
  }
  if (
    errors.fieldErrors &&
    errors.fieldErrors.length > 0 &&
    errorHandlers.fieldErrors
  ) {
    errors.fieldErrors.forEach((error) => {
      errorHandlers.fieldErrors(error);
    });
  }
}

type MutateOptions<Input, Payload> = {
  variables?: Input;
  callbacks?: CallbacksType<Payload>;
  refetchQueries?: (string | DocumentNode)[];
  awaitRefetchQueries?: boolean;
  optimisticResponse?: unknown;
  update?: MutationUpdaterFunction<Payload, Input, ApolloCache>;
  onCompleted?: (data: unknown) => void;
  onError?: (error: Error) => void;
};

export function useBackendMutation<Input, Payload>({
  mutation,
  variables,
  refetchQueries,
  errorHandlers,
  callbacks = {},
  optimisticResponse,
  update,
}: MutateBackendType<Input, Payload>) {
  const { isSignedIn } = useAuth();
  const setIsMutating = State((state) => state.setIsMutating);
  const hookCallbacksRef = useRef(callbacks);
  hookCallbacksRef.current = callbacks;
  const errorHandlersRef = useRef(errorHandlers);
  errorHandlersRef.current = errorHandlers;

  const [mutate, { loading }] = useMutation(mutation, {
    variables,
    refetchQueries,
    optimisticResponse,
    update,
  });

  const handleCompleted = useCallback(
    (
      response: Record<string, unknown> | null | undefined,
      callCallbacks?: CallbacksType<Payload>,
    ) => {
      const mergedCallbacks = {
        ...hookCallbacksRef.current,
        ...callCallbacks,
      };
      setIsMutating("success");
      const data = response ? response[Object.keys(response)[0]] : null;

      if (Values.IS_LOCAL_DEV_ENVIRONMENT) {
        console.log("onCompleted response: ", data);
      }

      if (!data) {
        mergedCallbacks.onError?.();
        return;
      }
      const errors = (data as { errors?: GraphqlPayloadErrors | null }).errors;

      if (!errors) {
        const asyncCallbacks = [];
        if (mergedCallbacks.onSuccess) {
          asyncCallbacks.push(
            mergedCallbacks.onSuccess(
              (data as { data?: Payload }).data as Payload,
            ),
          );
        }

        Promise.all(asyncCallbacks).catch(({ ...objs }) => {
          if (Values.IS_LOCAL_DEV_ENVIRONMENT) {
            console.error("onSuccess or onSuccessDispatch errors", {
              ...objs,
            });
          }
        });
        return;
      }

      const setSnackbarMessage = State.getState().setSnackbarMessage;
      const mergedHandlers = {
        ...buildDefaultErrorHandlers(setSnackbarMessage, !!isSignedIn),
        ...(errorHandlersRef.current || {}),
      };
      handleErrors(errors, mergedHandlers);
      mergedCallbacks.onError?.(undefined, errors);
    },
    [isSignedIn, setIsMutating],
  );

  const handleError = useCallback(
    (error: Error, callCallbacks?: CallbacksType<Payload>) => {
      const mergedCallbacks = {
        ...hookCallbacksRef.current,
        ...callCallbacks,
      };
      setIsMutating("error");
      State.getState().setSnackbarMessage(formatApolloError(error));
      if (Values.IS_LOCAL_DEV_ENVIRONMENT) {
        console.error(error);
      }
      mergedCallbacks.onError?.();
    },
    [setIsMutating],
  );

  const mutateCallback = useCallback(
    (options?: MutateOptions<Input, Payload>) => {
      const {
        callbacks: callCallbacks,
        onCompleted: apolloOnCompleted,
        onError: apolloOnError,
        ...apolloOptions
      } = options ?? {};
      setIsMutating("in-flight");
      return mutate({
        ...apolloOptions,
        onCompleted: (response) => {
          handleCompleted(response as Record<string, unknown>, callCallbacks);
          apolloOnCompleted?.(response);
        },
        onError: (error) => {
          handleError(error, callCallbacks);
          apolloOnError?.(error);
        },
      });
    },
    [handleCompleted, handleError, mutate, setIsMutating],
  );
  return {
    loading,
    mutate: mutateCallback,
  };
}
